import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import { BACKTESTING_IN_PROGRESS_MESSAGE } from "@shared/features/bots/BotForm/common/constants";
import AnimatedInProgressIcon from "common/Animations/AnimatedInProgressIcon";
import React from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { LoaderCircle } from "lucide-react";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { typography } from "@shared/common/Themes/typography";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const InProgressMessage = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const theme = useTheme();

  if (theme.isNewLook) {
    // New look: a quiet notice in a well, with the running spinner.
    return (
      <NewLookNotice role="status">
        <Spinner
          size={16}
          strokeWidth={2}
          color={getNewLookTokens(theme).accent}
        />
        <span>{t(BACKTESTING_IN_PROGRESS_MESSAGE)}</span>
      </NewLookNotice>
    );
  }

  return (
    <Wrapper $isMobile={isMobile}>
      <Flex>
        <Text
          $size={isMobile ? typography.fontSizes.S : typography.fontSizes.M}
        >
          {t(BACKTESTING_IN_PROGRESS_MESSAGE)}
        </Text>
        <Flex>
          <AnimatedInProgressIcon
            style={{
              marginLeft: 8,
              width: isMobile ? 30 : 45,
              height: isMobile ? 30 : 45,
            }}
          />
        </Flex>
      </Flex>
    </Wrapper>
  );
};

export default InProgressMessage;

const Wrapper = styled.div<{ $isMobile?: boolean }>`
  background: ${(p) => p.theme.colors.lightBlue};
  border-radius: 8px;
  padding: ${(p) => (p.$isMobile ? 12 : 20)}px;
  margin-bottom: 16px;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;
const NewLookNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Spinner = styled(LoaderCircle)`
  flex: none;
  margin-top: 2px;
  animation: ${spin} 1.2s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
